import { isDarkModeAtom, themeAtom } from "../shared/atoms";
import { SunIcon, MoonIcon } from "./Icons";
import { darkModeToThemePreference, saveThemePreference } from "./user-prefs.service";
import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue } from "jotai";
import React, { useCallback, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

type ThemeSwitchProps = {
  className?: string;
  userEmail?: string;
};

type ToggleSource = "sun" | "moon" | "switch" | "keyboard";

// Shows the current theme beside the toggle; the header owns placement with the user controls.
export const ThemeSwitch: React.FC<ThemeSwitchProps> = ({ className, userEmail }) => {
  const [isDarkMode, setIsDarkMode] = useAtom(isDarkModeAtom);
  const theme = useAtomValue(themeAtom);

  useEffect(() => {
    if (userEmail) {
      datadogRum.setUser({ id: userEmail, email: userEmail });
    }
  }, [userEmail]);

  useEffect(() => {
    datadogRum.setUserProperty("theme_preference", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Per-component/session switch counter (optional)
  const switchCountRef = useRef(0);

  const eventId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const persistThemePreference = useCallback((nextIsDarkMode: boolean) => {
    saveThemePreference(darkModeToThemePreference(nextIsDarkMode)).catch((error) => {
      console.error("Failed to save theme preference", error);
    });
  }, []);

  const onToggle = useCallback(
    (source: ToggleSource) => {
      setIsDarkMode((prev) => {
        const next = !prev;
        switchCountRef.current += 1;

        // Emit ONE custom action per toggle – Datadog will record each call
        datadogRum.addAction("theme_switch", {
          event_id: eventId(), // helps you verify uniqueness in Explorer
          ts: Date.now(), // optional explicit timestamp
          switch_index: switchCountRef.current, // per-component/session counter
          source, // sun | moon | switch | keyboard
          from: prev ? "dark" : "light",
          to: next ? "dark" : "light",
        });

        // Keep the user's current preference updated on their profile
        datadogRum.setUserProperty("theme_preference", next ? "dark" : "light");
        persistThemePreference(next);

        return next;
      });
    },
    [persistThemePreference, setIsDarkMode],
  );

  return (
    <div data-testid="theme-switcher" className={twMerge("flex shrink-0 items-center text-green", className)}>
      <button
        type="button"
        data-testid="theme-switcher-icon"
        onClick={() => onToggle(isDarkMode ? "moon" : "sun")}
        className={twMerge("mr-2 rounded-lg p-0.5", theme.headerComponent.notifications.iconBackground)}
        aria-label={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle("keyboard");
          }
        }}
      >
        {isDarkMode ? <MoonIcon /> : <SunIcon />}
      </button>

      <div
        data-testid="theme-switcher-toggle"
        onClick={() => onToggle("switch")}
        className={twMerge(
          "bg-greyscale-20 relative h-[18px] w-8 cursor-pointer rounded-full border-2 p-[2px]",
          theme.headerComponent.themeSwitch.switchBorder,
        )}
        role="switch"
        aria-label="Theme switch"
        aria-checked={isDarkMode}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle("keyboard");
          }
        }}
      >
        <div
          className={twMerge(
            "absolute top-1/2 h-[10px] w-[10px] -translate-y-1/2 rounded-full transition-transform duration-300",
            theme.headerComponent.themeSwitch.switchDot,
            isDarkMode ? "translate-x-[14px]" : "translate-x-0",
          )}
        />
      </div>
    </div>
  );
};

export default ThemeSwitch;
